import { useTranslation } from "react-i18next";
import { Info, SearchX } from "lucide-react";
import { Button } from "components/ui/button";
import { CategoryNotice } from "../../../../../api/apartment/categoryOutcome";
import { categoryLabel } from "../../../../home/homeTiles";

/**
 * The two explanations the search step owes when a link's `category` did not
 * mean what the customer expected — #2764 F9/F10, wording by the PO
 * (2026-09-25).
 *
 * Both exist because an unexplained screen is the failure this repo keeps
 * re-learning: an archived category's empty list must not read as "no rooms
 * free on your dates", and an ignored key must not leave someone with an old
 * bookmark wondering why they see more than they expected.
 *
 * **The URL's value is never rendered.** The archived message shows the name
 * from OUR data, resolved by the home tiles' own label rule; the unknown-key
 * note shows no name at all.
 */

export interface CategoryNoticeKeys {
  /** "Alle Veranstaltungsräume anzeigen" — flow-specific noun. */
  showAll: string;
  /** "… Du siehst alle Veranstaltungsräume." — flow-specific noun. */
  unknown: string;
}

interface ArchivedCategoryStateProps {
  notice: Extract<CategoryNotice, { kind: "archived" }>;
  keys: CategoryNoticeKeys;
  onShowAll: () => void;
}

/** An archived category: the empty list, explained, with a way onward. */
export const ArchivedCategoryState = ({
  notice,
  keys,
  onShowAll,
}: ArchivedCategoryStateProps) => {
  const { t, i18n } = useTranslation();
  const name = categoryLabel(notice, i18n.language ?? "");

  return (
    <div
      role="status"
      className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center"
    >
      <SearchX className="h-8 w-8 text-foreground/40" aria-hidden="true" />
      <h3 className="text-lg font-semibold text-foreground">
        {t("book_location.category_notice.archived_title")}
      </h3>
      <p className="text-sm text-foreground/70">
        {t("book_location.category_notice.archived_body", { name })}
      </p>
      <Button variant="outline" className="mt-2" onClick={onShowAll}>
        {t(keys.showAll)}
      </Button>
    </div>
  );
};

/** An unknown key: every object is shown; this says why. */
export const UnknownCategoryNote = ({ keys }: { keys: CategoryNoticeKeys }) => {
  const { t } = useTranslation();
  return (
    <p
      role="status"
      className="mb-3 flex items-start gap-2 text-sm text-foreground/70"
    >
      <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{t(keys.unknown)}</span>
    </p>
  );
};
